import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useCurrency } from "@/context/CurrencyContext";
import ProductImage from "@/components/ProductImage";
import { ArrowRight, Globe, ShieldCheck, Leaf, Package, Sparkle } from "@phosphor-icons/react";

const CATEGORIES = [
  { name: "Pepper", img: "/spices/pepper.webp", tag: "Bold heat" },
  { name: "Cardamom", img: "/spices/cardamom.webp", tag: "Queen of spice" },
  { name: "Saffron", img: "/spices/saffron.webp", tag: "Red gold" },
  { name: "Cinnamon", img: "/spices/cinnamon.webp", tag: "Warm sweet" },
  { name: "Turmeric", img: "/spices/turmeric.webp", tag: "Golden root" },
  { name: "Chilli", img: "/spices/chilli.webp", tag: "Slow burn" },
];

const STATS = [
  { n: "10K+", l: "Tons sourced annually" },
  { n: "40", l: "Countries of origin" },
  { n: "500+", l: "Trusted farms" },
  { n: "A+", l: "Grade verification" },
];

const TESTIMONIALS = [
  { name: "Aarav Menon", role: "Head Buyer, Alvora Foods NYC", quote: "We replaced three commodity brokers with WorldFoods2U. Traceability is unmatched and margins improved 14%.", img: "https://images.pexels.com/photos/35069661/pexels-photo-35069661.jpeg?auto=compress&cs=tinysrgb&w=400" },
  { name: "Isabela Correia", role: "Procurement, Nordica Spice Co.", quote: "Anonymous fulfillment keeps our supply chain honest. Quality is consistent across twelve shipments.", img: "https://images.pexels.com/photos/29898836/pexels-photo-29898836.jpeg?auto=compress&cs=tinysrgb&w=400" },
  { name: "Kenji Watanabe", role: "Imports Director, Okami Corp Tokyo", quote: "The PDP detail and grade system let my team close deals without flying to origin. Game changer.", img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400" },
];

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } };

export default function Home() {
  const { format, code } = useCurrency();
  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[100vh] hero-gradient overflow-hidden noise">
        <div className="absolute inset-0 opacity-25">
          <img
            src="/spices/turmeric.webp"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-ink-950 via-ink-950/70 to-transparent" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-36 md:pt-44 pb-24 grid md:grid-cols-12 gap-10">
          <motion.div {...fadeUp} className="md:col-span-7">
            <p className="section-label mb-6">Global B2B Spice Marketplace • Est. 2025</p>
            <h1 className="font-display font-light text-5xl md:text-6xl lg:text-[96px] leading-[0.95] tracking-tighter text-cream">
              From a single <em className="text-gradient-gold not-italic">farm</em>,
              <br />to every <em className="text-gradient-gold not-italic">global shelf</em>.
            </h1>
            <p className="mt-8 text-cream/70 text-lg max-w-xl leading-relaxed">
              The first anonymous B2B marketplace for verified spices and agri produce. Farmers list. Buyers source. We move grade-A origin to your shelf — with zero middlemen.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/shop" className="btn-primary text-sm" data-testid="hero-cta-shop">
                Explore Marketplace <ArrowRight size={16} weight="bold" />
              </Link>
              <Link to="/login" className="btn-secondary text-sm" data-testid="hero-cta-join">
                Join as Farmer
              </Link>
            </div>
            <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
              {STATS.map((s, i) => (
                <motion.div key={s.l} {...fadeUp} transition={{ duration: 0.6, delay: 0.1 * i }}>
                  <div className="font-display text-4xl md:text-5xl text-gradient-gold tracking-tight">{s.n}</div>
                  <div className="text-xs text-muted mt-1 tracking-wide">{s.l}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div {...fadeUp} transition={{ delay: 0.2 }} className="md:col-span-5 flex items-end">
            <div className="w-full glass rounded-3xl p-6 md:p-8 relative overflow-hidden">
              <div className="section-label mb-4">Featured Origin</div>
              <img src="/spices/saffron.webp" alt="Saffron" className="rounded-2xl aspect-[5/4] w-full object-cover" />
              <div className="mt-5">
                <div className="flex items-center gap-2 text-xs text-gold uppercase tracking-[0.2em]">Iran · Khorasan</div>
                <h3 className="font-display text-3xl text-cream mt-1 leading-tight">Negin Saffron</h3>
                <p className="text-sm text-muted mt-2">All-red threads. Coupe-grade. 12kg available this harvest.</p>
                <div className="mt-4 flex items-center justify-between">
                  <span className="font-display text-2xl text-saffron">{format(1850)}<span className="text-sm text-muted">/kg {code}</span></span>
                  <Link to="/shop" className="text-sm text-gold hover:text-saffron transition-colors">View →</Link>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* ticker */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-white/5 bg-ink-900/60 backdrop-blur-sm overflow-hidden">
          <div className="marquee py-4 text-xs tracking-[0.3em] uppercase text-gold/70 whitespace-nowrap">
            {[...Array(2)].map((_, k) => (
              <div key={k} className="flex gap-12 items-center">
                <span>Kerala Tellicherry</span><span>•</span>
                <span>Sri Lanka Ceylon</span><span>•</span>
                <span>Iran Negin</span><span>•</span>
                <span>Madagascar Vanilla</span><span>•</span>
                <span>Vietnam Star Anise</span><span>•</span>
                <span>Ethiopia Arabica</span><span>•</span>
                <span>Grenada Nutmeg</span><span>•</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUE PILLARS */}
      <section className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10">
          <motion.div {...fadeUp} className="md:col-span-4">
            <p className="section-label">Why WorldFoods2U</p>
            <h2 className="font-display text-4xl md:text-5xl mt-4 tracking-tight leading-[1.05]">
              A <em className="italic text-saffron">quiet revolution</em> in the spice trade.
            </h2>
          </motion.div>
          <div className="md:col-span-8 grid sm:grid-cols-2 gap-5">
            {[
              { icon: ShieldCheck, title: "Anonymous fulfillment", text: "Buyers never see farmer identity. Zero back-channel pricing, zero leakage." },
              { icon: Globe, title: "40 origin countries", text: "From Kerala hills to Ethiopian highlands, directly verified by our agronomists." },
              { icon: Leaf, title: "Organic & fairtrade", text: "Every listing specifies certification, grade, expiry and shipping terms upfront." },
              { icon: Package, title: "Escrow payments", text: "Money is held safely until the farmer ships with valid tracking. No disputes." },
            ].map((p, i) => (
              <motion.div key={p.title} {...fadeUp} transition={{ duration: 0.6, delay: i * 0.1 }} className="glass rounded-2xl p-7">
                <p.icon size={32} weight="duotone" className="text-saffron" />
                <h3 className="font-display text-2xl mt-5 text-cream">{p.title}</h3>
                <p className="text-sm text-muted mt-2 leading-relaxed">{p.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="py-24 md:py-32 bg-ink-900 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between mb-14">
            <div>
              <p className="section-label">The Collection</p>
              <h2 className="font-display text-4xl md:text-5xl mt-3 tracking-tight">Eleven spices. One vault.</h2>
            </div>
            <Link to="/shop" className="hidden md:inline text-sm text-gold hover:text-saffron">Browse all →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
            {CATEGORIES.map((c, i) => (
              <motion.div key={c.name} {...fadeUp} transition={{ duration: 0.6, delay: i * 0.08 }}>
                <Link to={`/shop?category=${encodeURIComponent(c.name)}`} className="group relative block aspect-[4/5] rounded-2xl overflow-hidden bg-ink-950" data-testid={`cat-${c.name.toLowerCase()}`}>
                  <ProductImage src={c.img} alt={c.name} className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{c.tag}</p>
                    <h3 className="font-display text-3xl md:text-4xl text-cream mt-1">{c.name}</h3>
                  </div>
                  <div className="absolute top-5 right-5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-10 h-10 rounded-full bg-saffron text-ink-950 flex items-center justify-center">
                      <ArrowRight size={18} weight="bold" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-10">
        <motion.div {...fadeUp}>
          <p className="section-label">How it works</p>
          <h2 className="font-display text-4xl md:text-6xl mt-4 tracking-tighter max-w-3xl leading-[1.02]">
            Four steps. <em className="text-gradient-gold not-italic">Zero middlemen.</em>
          </h2>
        </motion.div>
        <div className="mt-14 grid md:grid-cols-4 gap-6">
          {[
            { n: "01", t: "Farmer lists", d: "Stock, grade, origin, certifications. All verified." },
            { n: "02", t: "Buyer sources", d: "Branded listings. No farmer contact. Transparent pricing." },
            { n: "03", t: "Escrow payment", d: "Funds held until shipment with valid tracking." },
            { n: "04", t: "Ship to shelf", d: "Direct farm → port → global shelf. With insurance." },
          ].map((s, i) => (
            <motion.div key={s.n} {...fadeUp} transition={{ duration: 0.6, delay: i * 0.1 }} className="relative">
              <div className="font-display text-6xl text-saffron/30 leading-none">{s.n}</div>
              <h3 className="font-display text-2xl mt-3 text-cream">{s.t}</h3>
              <p className="text-sm text-muted mt-2 leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 md:py-32 relative bg-gradient-to-b from-ink-900 to-ink-950">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto">
            <p className="section-label">Trusted by procurement leaders</p>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight mt-4">Voices from the vault.</h2>
          </motion.div>
          <div className="mt-16 grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <motion.div key={t.name} {...fadeUp} transition={{ duration: 0.6, delay: i * 0.1 }} className="glass rounded-2xl p-8 flex flex-col">
                <Sparkle size={24} weight="fill" className="text-gold" />
                <p className="font-display text-xl md:text-2xl text-cream mt-5 leading-snug">"{t.quote}"</p>
                <div className="mt-6 flex items-center gap-3">
                  <img src={t.img} alt={t.name} className="w-11 h-11 rounded-full object-cover" />
                  <div>
                    <div className="text-sm text-cream font-medium">{t.name}</div>
                    <div className="text-xs text-muted">{t.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <motion.div {...fadeUp}>
            <p className="section-label">Ready to source?</p>
            <h2 className="font-display text-5xl md:text-7xl tracking-tighter mt-4 leading-[1.0]">
              Open the <em className="text-gradient-gold not-italic">Vault</em>.
            </h2>
            <p className="text-muted mt-6 text-lg max-w-2xl mx-auto">
              Two minute signup. Google SSO. Free for first three orders.
            </p>
            <div className="mt-10 flex items-center justify-center gap-4 flex-wrap">
              <Link to="/shop" className="btn-primary" data-testid="cta-shop">Enter Marketplace <ArrowRight size={16} weight="bold" /></Link>
              <Link to="/login" className="btn-secondary" data-testid="cta-login">Sign In</Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
